import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Container } from '@/components/layout/Container'
import { ProductCard } from '@/components/fragrance/ProductCard'
import { useAuthStore, getGoogleAuthUrl } from '@/stores/authStore'
import { useScentProfileStore } from '@/stores/scentProfileStore'
import { fetchWishlist, removeFromWishlist, getCatalogue } from '@/lib/api'
import { Heart, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Wishlist() {
  const { user, loading: authLoading, fetchUser } = useAuthStore()
  const { wishlistIds, setWishlistIds, result } = useScentProfileStore()
  const [removing, setRemoving] = useState<Set<string>>(new Set())

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  useEffect(() => {
    if (!user) return
    let cancelled = false
    fetchWishlist().then((slugs) => {
      if (!cancelled) setWishlistIds(slugs)
    })
    return () => {
      cancelled = true
    }
  }, [user, setWishlistIds])

  const [perfumes, setPerfumes] = useState<any[]>([])
  useEffect(() => {
    getCatalogue().then((d)=>setPerfumes(d.products))
  }, [])

  const wishlist = wishlistIds
    .map((id) => perfumes.find((p)=>p.id===id))
    .filter((p): p is NonNullable<typeof p> => !!p)

  const recommendations = result
    ? perfumes
        .filter((p) => p.family === result.primary && !wishlistIds.includes(p.id))
        .slice(0, 3)
    : perfumes.filter((p) => p.isBestseller && !wishlistIds.includes(p.id)).slice(0, 3)

  async function handleRemove(id: string) {
    setRemoving((prev) => new Set(prev).add(id))
    try {
      const slugs = await removeFromWishlist(id)
      setWishlistIds(slugs)
    } finally {
      setRemoving((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  if (authLoading) {
    return (
      <div className="pt-28 pb-24">
        <Container className="max-w-6xl">
          <div className="h-8 w-48 animate-pulse bg-ink/10" />
        </Container>
      </div>
    )
  }

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-6xl">
        <h1 className="mb-2 font-display text-4xl normal-case text-carbon">wishlist</h1>

        {!user ? (
          wishlist.length === 0 ? (
            <div className="mx-auto mt-12 max-w-md text-center">
              <div className="mb-6 flex justify-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-ink/10 bg-wd-mist">
                  <Heart className="h-8 w-8 text-wd-orange" />
                </div>
              </div>
              <h2 className="mb-3 font-display text-2xl normal-case text-carbon">
                Save your favourites
              </h2>
              <p className="mb-8 text-carbon/60">
                Sign in to keep track of the scents you love and find them quickly later.
              </p>
              <div className="flex flex-col items-center gap-3">
                <a
                  href={getGoogleAuthUrl()}
                  className="inline-flex w-full items-center justify-center border border-ink/10 bg-ink px-6 py-3 text-sm text-white transition-colors hover:bg-wd-orange"
                >
                  Sign in to view your wishlist
                </a>
                <Link
                  to="/shop"
                  className="inline-flex w-full items-center justify-center border border-ink/10 bg-white px-6 py-3 text-sm text-carbon transition-colors hover:border-ink/30"
                >
                  Continue browsing
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-8">
              <div className="mb-6 border border-ink/10 bg-wd-mist p-4">
                <p className="text-sm text-carbon/80">
                  <strong>You have {wishlist.length} saved item{wishlist.length === 1 ? '' : 's'} on this device.</strong>{' '}
                  Sign in to keep them safe across all your devices.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a
                    href={getGoogleAuthUrl()}
                    className="inline-flex items-center justify-center border border-ink/10 bg-ink px-5 py-2 text-xs text-white transition-colors hover:bg-wd-orange"
                  >
                    Sign in to save
                  </a>
                  <Link
                    to="/shop"
                    className="inline-flex items-center justify-center border border-ink/10 bg-white px-5 py-2 text-xs text-carbon transition-colors hover:border-ink/30"
                  >
                    Continue browsing
                  </Link>
                </div>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {wishlist.map((p, i) => (
                  <div key={p.id} className="relative">
                    <ProductCard perfume={p} index={i} />
                    <button
                      onClick={() => {
                        setWishlistIds(wishlistIds.filter((id) => id !== p.id))
                      }}
                      className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border bg-white text-carbon shadow-sm transition-colors hover:border-wd-orange hover:text-wd-orange"
                      aria-label={`Remove ${p.name} from wishlist`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : wishlist.length === 0 ? (
          <div className="mt-12">
            <div className="mb-10 max-w-md">
              <p className="text-carbon/60">
                Your wishlist is empty. Products you love will appear here.
              </p>
            </div>
            {recommendations.length > 0 && (
              <div>
                <h2 className="mb-6 font-display text-2xl normal-case text-carbon">
                  {result ? 'Recommended for your scent profile' : 'You may also love'}
                </h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {recommendations.map((p, i) => (
                    <ProductCard key={p.id} perfume={p} index={i} />
                  ))}
                </div>
              </div>
            )}
            <div className="mt-10">
              <Link
                to="/shop"
                className="inline-flex items-center justify-center gap-2 border border-ink/30 px-6 py-3 text-xs font-medium normal-case tracking-[0.12em] text-carbon transition-all duration-300 hover:border-ink hover:bg-ink hover:text-white"
              >
                Explore all scents
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-8">
            <p className="mb-8 text-carbon/60">
              {wishlist.length} item{wishlist.length === 1 ? '' : 's'} saved
            </p>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {wishlist.map((p, i) => (
                <div key={p.id} className="relative">
                  <ProductCard perfume={p} index={i} />
                  <button
                    onClick={() => handleRemove(p.id)}
                    disabled={removing.has(p.id)}
                    className={cn(
                      'absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border bg-white text-carbon shadow-sm transition-colors',
                      removing.has(p.id)
                        ? 'cursor-not-allowed opacity-60'
                        : 'hover:border-wd-orange hover:text-wd-orange'
                    )}
                    aria-label={`Remove ${p.name} from wishlist`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </Container>
    </div>
  )
}
